import { FC, useMemo } from 'react';
import { useTranslation } from '@packages/utils';
import dayjs from 'dayjs';
import { EWorkshopDetailsAllowedActions } from 'data/types/workshops.types';
import { Col, Row } from 'components/Base/Grid';
import { useHasPartTimeErrors } from '../context/PartTimeValidationContext';
import { TActionsGroupProps } from './Actions.types';
import PublishAction from './PublishAction';
import ActionsGroupButton from './ActionsGroupButton';
import RescheduleAction from './RescheduleAction';
import RepublishAction from './RepublishAction';

const Actions: FC<TActionsGroupProps> = ({ id, data }) => {
  const { t } = useTranslation({
    keyPrefix:
      'descriptions.admin-portal.workshops.details.actions.publish.disabled-tooltip',
  });

  const incompleteSessionDates = useMemo(
    () =>
      data?.parts?.some(
        part => !part.date?.value || !part.startTime || !part.endTime,
      ) ?? false,
    [data?.parts],
  );

  // BE (GROW-4212) rejects publish/republish when any part's end time is
  // not strictly greater than its start time. Check the same rule here so
  // the button greys out with a tooltip instead of the user hitting a 400.
  const invalidSessionTimeOrder = useMemo(
    () =>
      data?.parts?.some(part => {
        if (!part.startTime || !part.endTime) return false;
        const start = dayjs(part.startTime, 'HH:mm:ss');
        const end = dayjs(part.endTime, 'HH:mm:ss');
        return !end.isAfter(start, 'minute');
      }) ?? false,
    [data?.parts],
  );

  const noSiteCoordinators = data?.siteCoordinators?.length === 0;
  const noPhoto = !data?.photo.value;
  const noDescription = !data?.description?.value;
  const noClasses = !data?.classes.value?.length;
  const noTimezone = !data?.timezone;

  // ELC specific validations (only for non-virtual workshops)
  const isELC = data?.host?.hostType === 'ELC';
  const isVirtual = data?.attendanceType === 'VIRTUAL';
  const noLocation = isELC && !isVirtual && !data?.location?.address?.id;
  const noShippingAddress = isELC && !isVirtual && !data?.shipmentLocation?.id;
  const noMeetingLink = isVirtual && !data?.meetingLink?.value;

  // A part editor may be showing an End <= Start that was deliberately not saved,
  // so `data.parts` above still looks valid. Fold that in so the buttons grey out
  // while the invalid value is on screen — the originally reported GROW-4212 case.
  const hasUnsavedTimeErrors = useHasPartTimeErrors();
  const invalidTimeOrder = invalidSessionTimeOrder || hasUnsavedTimeErrors;

  // Derived rather than state-in-an-effect: the previous useEffect keyed only on
  // `data`, so it could not react to hasUnsavedTimeErrors, which comes from context.
  const isPublishDisabled =
    noSiteCoordinators ||
    incompleteSessionDates ||
    invalidTimeOrder ||
    noPhoto ||
    noDescription ||
    noClasses ||
    noTimezone ||
    noLocation ||
    noShippingAddress ||
    noMeetingLink;

  const disabledReason = useMemo(() => {
    if (incompleteSessionDates) return t('INCOMPLETE_SESSION_DATES');
    if (invalidTimeOrder) return t('INVALID_SESSION_TIME_ORDER');
    if (noTimezone) return t('NO_TIMEZONE');
    if (noSiteCoordinators) return t('NO_SITE_COORDINATORS');
    if (noDescription) return t('NO_DESCRIPTION');
    if (noPhoto) return t('NO_PHOTO');
    if (noClasses) return t('NO_CLASSES');
    if (noLocation) return t('NO_LOCATION');
    if (noShippingAddress) return t('NO_SHIPPING_ADDRESS');
    if (noMeetingLink) return t('NO_MEETING_LINK');
    return undefined;
  }, [
    t,
    incompleteSessionDates,
    invalidTimeOrder,
    noTimezone,
    noSiteCoordinators,
    noDescription,
    noPhoto,
    noClasses,
    noLocation,
    noShippingAddress,
    noMeetingLink,
  ]);

  // Republish shares the BE's validatePublish, so it must reject the same
  // states — including legacy workshops persisted before GROW-4212 with a
  // null endTime. Gate it on the presence + ordering checks only; the
  // publish-only requirements (photo, description, etc.) don't apply to
  // republish since a workshop that reached PUBLISHED had them once.
  const isRepublishDisabled = incompleteSessionDates || invalidTimeOrder;
  const republishDisabledReason = useMemo(() => {
    if (incompleteSessionDates) return t('INCOMPLETE_SESSION_DATES');
    if (invalidTimeOrder) return t('INVALID_SESSION_TIME_ORDER');
    return undefined;
  }, [t, incompleteSessionDates, invalidTimeOrder]);

  return (
    <Row gutter={[16, 16]}>
      {data?.allowedActions?.includes(
        EWorkshopDetailsAllowedActions.PUBLISH,
      ) && (
        <Col>
          <PublishAction
            id={id}
            disabled={isPublishDisabled}
            disabledReason={disabledReason}
          />
        </Col>
      )}
      {data?.allowedActions?.includes(
        EWorkshopDetailsAllowedActions.RESCHEDULE,
      ) && (
        <Col>
          <RescheduleAction id={id} />
        </Col>
      )}
      {data?.allowedActions?.includes(
        EWorkshopDetailsAllowedActions.REPUBLISH,
      ) && (
        <Col>
          <RepublishAction
            id={id}
            disabled={isRepublishDisabled}
            disabledReason={republishDisabledReason}
          />
        </Col>
      )}
      <Col>
        <ActionsGroupButton id={id} data={data} />
      </Col>
    </Row>
  );
};

export default Actions;
